import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { AuthInterceptor } from './services/auth/auth.interceptor';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BrowserModule } from '@angular/platform-browser';

import { AppComponent } from './app.component';
import { SharedModule } from './shared/shared.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  TuiAlertModule,
  TuiDialogModule,
  TuiRootModule,
  TUI_ALERT_POSITION,
} from '@taiga-ui/core';
import { TuiTagModule } from '@taiga-ui/kit';
import {
  CommonModule,
  HashLocationStrategy,
  LocationStrategy,
  registerLocaleData,
} from '@angular/common';

import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { AppRoutingModule } from './app-routing.module';
import { LocalStorageService } from './services/local-storage/local-storage.service';
import { LocalStorageKeys } from './enum/local-storage-keys.enum';
import { AuthService } from './services/auth/auth.service';

import {
  MsalModule,
  MSAL_INSTANCE,
  MsalService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
  MsalGuard,
} from '@azure/msal-angular';
import {
  PublicClientApplication,
  IPublicClientApplication,
  BrowserCacheLocation,
  InteractionType,
} from '@azure/msal-browser';

import { HomeModule } from './pages/home/home.module';
import { HttpClientModule } from '@angular/common/http';
import { LanguageEnum } from './enum/language.enum';
import { environment } from 'src/environments/environment';

import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeEn from '@angular/common/locales/en';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.auth.clientId,
      authority: environment.auth.authority,
      redirectUri: environment.auth.redirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: environment.auth.storeAuthStateInCookie,
    },
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: environment.auth.scopes,
    },
    loginFailedRoute: '/login-failed',
  };
}

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function initializeTranslations(
  translate: TranslateService,
  localStorageService: LocalStorageService
) {
  return (): Promise<any> => {
    const browserLang = navigator.language || (navigator as any).userLanguage;
    const langCode = browserLang?.toLowerCase().split('-')[0];
    
    const supportedLanguages: Record<string, LanguageEnum> = {
      'es': LanguageEnum.SPANISH,
      'en': LanguageEnum.ENGLISH,
      'fr': LanguageEnum.FRENCH
    };
    
    const browserLanguage = supportedLanguages[langCode] || null;
    const storedLanguage = localStorageService.get<LanguageEnum>(LocalStorageKeys.LANGUAGE);
    const language = storedLanguage ?? browserLanguage ?? LanguageEnum.DEFAULT;

    translate.setDefaultLang(language);
    return firstValueFrom(translate.use(language));
  };
}

export function initializeMsal(authService: AuthService) {
  return async (): Promise<void> => {
    try {
      await authService.initMsalInstance();
    } catch (error) {
      // Ignore initialization errors to allow app bootstrap
    }
  };
}

registerLocaleData(localeEs);
registerLocaleData(localeFr);
registerLocaleData(localeEn);


@NgModule({
  declarations: [AppComponent],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    TuiRootModule,
    TuiTagModule,
    TuiAlertModule,
    TuiDialogModule,
    SharedModule,
    AppRoutingModule,
    HomeModule,
    HttpClientModule,
    MsalModule,
    TranslateModule.forRoot({
      defaultLanguage: LanguageEnum.DEFAULT,
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    MsalGuard,
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    {
      provide: TUI_ALERT_POSITION,
      useValue: 'auto auto 2rem 3rem',
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    MsalService,
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTranslations,
      deps: [TranslateService, LocalStorageService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [AuthService],
      multi: true
    },
  ],
  bootstrap: [AppComponent],
})
// [ER - 10/03/2024] -  MsalRedirectComponent -  MsalRedirectComponent es un componente auxiliar que no debe inicializarse manualmente.
export class AppModule {}
